import React, { useState, useRef, useEffect } from 'react';
import { Checkdevice } from '../../../../../../components'


// A3 dimensions at 150dpi (1754×2480px)
const PAPER_WIDTH = 1754;
const PAPER_HEIGHT = 2480;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;

export default function SeatLayout() {
    // State management
    const [zoom, setZoom] = useState(1);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const [tables, setTables] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({ id: null, guests: '' });
    const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });

    // Refs
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    const draggingIdRef = useRef(null);
    const dragOffset = useRef({ x: 0, y: 0 });
    const isPanningRef = useRef(false);
    const panStartRef = useRef({ x: 0, y: 0 });
    const resizingIdRef = useRef(null);
    const initialResizeDistanceRef = useRef(null);

    const device = Checkdevice()

    // Initialize and fit to view
    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                const { width, height } = containerRef.current.getBoundingClientRect();
                setSvgSize({ width, height });
            }
        };

        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    const fitToView = () => {
        if (!containerRef.current) return;

        const container = containerRef.current.getBoundingClientRect();
        const scale = Math.min(
            (container.width - 40) / PAPER_WIDTH,
            (container.height - 40) / PAPER_HEIGHT
        );

        setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale)));
        setPanOffset({
            x: (container.width - PAPER_WIDTH * scale) / 2,
            y: (container.height - PAPER_HEIGHT * scale) / 2
        });
    };

    // Event handlers
    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        setZoom(prev => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev + delta)));
    };

    const handleAddTable = () => {
        setFormData({ id: null, guests: '' });
        setShowForm(true);
    };

    const handleFormSubmit = () => {
        const guests = parseInt(formData.guests, 10);
        if (isNaN(guests) || guests <= 0) return;

        if (formData.id !== null) {
            setTables(prev => prev.map(t => t.id === formData.id ? { ...t, guests } : t));
        } else {
            const id = Date.now();
            setTables(prev => [...prev, {
                id,
                guests,
                x: PAPER_WIDTH / 2,
                y: PAPER_HEIGHT / 2,
                radius: 50
            }]);
        }
        setShowForm(false);
    };

    const startDrag = (e, id) => {
        if (resizingIdRef.current !== null) return;
        draggingIdRef.current = id;
        const svg = svgRef.current;
        const pt = svg.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
        const table = tables.find(t => t.id === id);
        // Adjust for panOffset and zoom
        const adjustedX = (svgP.x - panOffset.x) / zoom;
        const adjustedY = (svgP.y - panOffset.y) / zoom;
        dragOffset.current = {
            x: adjustedX - table.x,
            y: adjustedY - table.y
        };
    };

    const handleResizeStart = (e, id) => {
        e.preventDefault();
        e.stopPropagation();
        resizingIdRef.current = id;

        const svg = svgRef.current;
        const pt = svg.createSVGPoint();
        pt.x = e.clientX; pt.y = e.clientY;
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
        const table = tables.find(t => t.id === id);

        initialResizeDistanceRef.current = Math.hypot(svgP.x - table.x, svgP.y - table.y);
    };

    const handleMouseMove = (e) => {
        const svg = svgRef.current;
        const pt = svg.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
        // Resize table
        if (resizingIdRef.current !== null) {
            const table = tables.find(t => t.id === resizingIdRef.current);
            const adjustedX = (svgP.x - panOffset.x) / zoom;
            const adjustedY = (svgP.y - panOffset.y) / zoom;
            const dist = Math.hypot(adjustedX - table.x, adjustedY - table.y);
            const newRadius = Math.max(30, Math.min(dist, 150));
            setTables(prev => prev.map(t =>
                t.id === resizingIdRef.current ? { ...t, radius: newRadius } : t
            ));
            return;
        }
        // Drag table
        if (draggingIdRef.current !== null) {
            const table = tables.find(t => t.id === draggingIdRef.current);
            // Adjust for panOffset and zoom
            const adjustedX = (svgP.x - panOffset.x) / zoom;
            const adjustedY = (svgP.y - panOffset.y) / zoom;
            const rawX = adjustedX - dragOffset.current.x;
            const rawY = adjustedY - dragOffset.current.y;
            // Constrain to paper bounds
            const minX = table.radius;
            const maxX = PAPER_WIDTH - table.radius;
            const minY = table.radius;
            const maxY = PAPER_HEIGHT - table.radius;
            const clampedX = Math.min(maxX, Math.max(minX, rawX));
            const clampedY = Math.min(maxY, Math.max(minY, rawY));
            setTables(prev => prev.map(t =>
                t.id === draggingIdRef.current ? { ...t, x: clampedX, y: clampedY } : t
            ));
            console.log('Dragging:', { zoom, client: [e.clientX, e.clientY], svgP, adjustedX, adjustedY, rawX, rawY, clampedX, clampedY });
            return;
        }
        // Pan canvas
        if (isPanningRef.current) {
            const dx = e.clientX - panStartRef.current.x;
            const dy = e.clientY - panStartRef.current.y;
            setPanOffset(prev => ({
                x: prev.x + dx,
                y: prev.y + dy
            }));
            panStartRef.current = { x: e.clientX, y: e.clientY };
        }
    };

    const handleEdit = (id) => {
        const table = tables.find(t => t.id === id);
        if (table) {
            setFormData({ id: table.id, guests: table.guests.toString() });
            setShowForm(true);
        }
    };

    // Export to PNG
    const exportToPNG = () => {
        const svgElement = svgRef.current;
        const serializer = new XMLSerializer();

        // Create clone for export
        const clone = svgElement.cloneNode(true);
        const paperGroup = clone.querySelector('#paper-group');

        // Set up export dimensions
        clone.setAttribute('width', PAPER_WIDTH);
        clone.setAttribute('height', PAPER_HEIGHT);
        clone.setAttribute('viewBox', `0 0 ${PAPER_WIDTH} ${PAPER_HEIGHT}`);
        clone.replaceChildren(paperGroup);

        const svgStr = serializer.serializeToString(clone);
        const canvas = document.createElement('canvas');
        canvas.width = PAPER_WIDTH;
        canvas.height = PAPER_HEIGHT;

        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);

            const link = document.createElement('a');
            link.download = 'seating-chart-a3.png';
            link.href = canvas.toDataURL('image/png');
            link.click();
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgStr)));
    };

    // Render table component
    const renderTable = (table) => {
        const guestRadius = 20;
        const guests = [];
        for (let i = 0; i < table.guests; i++) {
            const angle = (2 * Math.PI / table.guests) * i;
            const x = table.x + (table.radius + 40) * Math.cos(angle);
            const y = table.y + (table.radius + 40) * Math.sin(angle);
            guests.push(<circle key={i} cx={x} cy={y} r={guestRadius} fill="#fff" stroke='#333333' />);
        }

        return (
            <g
                key={table.id}
                onMouseDown={e => startDrag(e, table.id)}
                onDoubleClick={() => handleEdit(table.id)}
                style={{ cursor: 'grab' }}
            >
                {guests}
                <circle
                    cx={table.x}
                    cy={table.y}
                    r={table.radius}
                    fill="#fff"
                    stroke={draggingIdRef.current === table.id ? '#f00' : '#333'} // Red stroke when dragging
                    strokeWidth={2}
                />
                <text x={table.x} y={table.y + 4} textAnchor="middle" fontSize="14" fill="#333" fontFamily='Lato'>
                    Table ({table.guests})
                </text>
                <circle
                    cx={table.x + table.radius + 10}
                    cy={table.y}
                    r={6}
                    fill="black"
                    onMouseDown={e => handleResizeStart(e, table.id)}
                    style={{ cursor: 'pointer' }}
                />
                <text
                    x={table.x + table.radius + 10}
                    y={table.y}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#fff"
                    pointerEvents="none"
                >
                    ↑
                </text>
            </g>
        );
    };

    return (
        <div style={{ position: 'relative', width: '100%', top: 60 }}>
            <div
                ref={containerRef}
                style={{
                    width: `calc( 100% - ${{ 'WEB': 400, 'TABLET': 270, 'MOBILE': 0 }[device]}px )`,
                    height: 'calc( 100vh - 60px )',
                    padding: 10,
                    fontFamily: 'Arial',
                    display: 'flex',
                    flexDirection: 'row',
                }}
            >


                {/* SVG Canvas */}
                <div
                    style={{
                        flex: 1,
                        border: '1px solid #ccc',
                        backgroundColor: '#f0f0f0',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <svg
                        ref={svgRef}
                        width="100%"
                        height="100%"
                        viewBox={`0 0 ${PAPER_WIDTH} ${PAPER_HEIGHT} `} // Set viewBox to A3 dimensions
                        preserveAspectRatio="xMidYMid meet"
                        onWheel={handleWheel}
                        onMouseDown={(e) => {
                            if (
                                e.target.tagName === 'svg' ||
                                e.target.id === 'paper-bg' ||
                                e.target.id === 'grid-layer'
                            ) {
                                isPanningRef.current = true;
                                panStartRef.current = { x: e.clientX, y: e.clientY };
                            }
                        }}
                        onMouseMove={handleMouseMove}
                        onMouseUp={() => {
                            draggingIdRef.current = null;
                            resizingIdRef.current = null;
                            isPanningRef.current = false;
                        }}
                        onMouseLeave={() => {
                            draggingIdRef.current = null;
                            resizingIdRef.current = null;
                            isPanningRef.current = false;
                        }}
                        style={{ cursor: isPanningRef.current ? 'grabbing' : 'default' }}
                    >
                        <defs>
                            <pattern id="grid" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                                <path d="M 80 0 L 0 0 0 80" fill="none" stroke="#555" strokeWidth="1.5" />
                            </pattern>
                        </defs>
                        <g transform={`translate(${panOffset.x} ${panOffset.y}) scale(${zoom})`}>
                            <g id="paper-group">
                                <rect
                                    id="paper-bg"
                                    x={0}
                                    y={0}
                                    width={PAPER_WIDTH}
                                    height={PAPER_HEIGHT}
                                    fill="white"
                                    stroke="#999"
                                    strokeWidth="1"
                                    opacity="0.85"
                                />
                                <rect
                                    id="grid-layer"
                                    x={0}
                                    y={0}
                                    width={PAPER_WIDTH}
                                    height={PAPER_HEIGHT}
                                    fill="url(#grid)"
                                    pointerEvents="none"
                                />
                                {tables.map(renderTable)}
                            </g>
                        </g>
                    </svg>


                    {/* Control buttons (unchanged) */}

                    <div style={{ display: 'flex', flexDirection: 'column', position: 'absolute', top: 0, right: 0 }}>
                        <button onClick={handleAddTable} style={{ ...buttonStyle, backgroundColor: '#28a745' }}>
                            Add Table
                        </button>
                        <button onClick={exportToPNG} style={{ ...buttonStyle, backgroundColor: '#6c757d' }}>
                            Export A3 PNG
                        </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', position: 'absolute', bottom: 0, right: 0 }}>
                        <button onClick={fitToView} style={buttonStyle}>
                            Fit to View
                        </button>
                        <button onClick={() => setZoom(z => Math.min(MAX_ZOOM, z + ZOOM_STEP))} style={buttonStyle}>
                            Zoom In
                        </button>
                        <button onClick={() => setZoom(z => Math.max(MIN_ZOOM, z - ZOOM_STEP))} style={buttonStyle}>
                            Zoom Out
                        </button>
                    </div>
                </div>


                

                {/* Table Form (unchanged) */}
                {showForm && (
                    <div
                        style={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            backgroundColor: '#fff',
                            borderTop: '1px solid #ccc',
                            padding: 16,
                            boxShadow: '0 -2px 6px rgba(0,0,0,0.1)',
                        }}
                    >
                        <label style={{ display: 'block', marginBottom: 8 }}>
                            Number of Guests:
                            <input
                                type="number"
                                value={formData.guests}
                                onChange={e => setFormData({ ...formData, guests: e.target.value })}
                                style={{
                                    marginLeft: 8,
                                    padding: '4px 8px',
                                    fontSize: 14,
                                    border: '1px solid #ccc',
                                    borderRadius: 4,
                                }}
                            />
                        </label>
                        <button onClick={handleFormSubmit} style={{ ...buttonStyle, backgroundColor: '#007bff' }}>
                            Save
                        </button>
                    </div>
                )}
            </div>
            <div style={{ width: { 'WEB': 400, 'TABLET': 270, 'MOBILE': 0 }[device] }}>
                                
            </div>
        </div>
    );

}

// Button styles
const buttonStyle = {
    padding: '8px 16px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
};